import { useId } from '@floating-ui/react';
import { Button } from '@hologram-dimension/button';
import { Link } from '@hologram-dimension/link';
import { Panel } from '@hologram-dimension/panel';
import { DdsTypographyFontFamilyDefault } from '@hologram-dimension/tokens';
import {
  BarController,
  BarElement,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartEvent,
  type ChartOptions,
  type LegendElement,
  type LegendItem,
} from 'chart.js';
import { DateTime } from 'luxon';
import { useCallback, useMemo, type FC } from 'react';
import { Scatter } from 'react-chartjs-2';
import useOrganization from 'state/contexts/Organization/useOrganization';
import { LegacyUsageDataRecord } from 'types/legacyDashboard';
import { getUserFriendlyBytes } from 'utils/bytes';
import useSimUrl from 'utils/hooks/useSimUrl';
import useOrgUrl from 'utils/legacy/useOrgUrl';
import formatNumber from 'utils/numbers/formatNumber';
import styles from './SimRecentDataSessionsChartPanel.module.css';

// Chart JS plugins have to load after
import 'chartjs-adapter-luxon';
import 'chartjs-scale-timestack';

ChartJS.register([
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Tooltip,
  Legend,
]);

type LegendClickHandler = (
  this: LegendElement<'scatter'>,
  e: ChartEvent,
  legendItem: LegendItem,
  legend: LegendElement<'scatter'>
) => void;

interface SimRecentDataSessionsChartProps {
  mode: 'sim' | 'organization';
  hologramSimId?: HologramSimId;
  usageData: Array<LegacyUsageDataRecord>;
  onLoadMoreClick: AnyFunction;
  loading: boolean;
  hasMore: boolean;
  simDetailBaseUrl: string;
  pageSize: number;
  usageByRecentSessionUrl: string;
}

const SimRecentDataSessionsChartPanel: FC<SimRecentDataSessionsChartProps> = ({
  mode,
  hologramSimId,
  usageData,
  onLoadMoreClick,
  loading,
  hasMore,
  simDetailBaseUrl,
  pageSize,
  usageByRecentSessionUrl,
}) => {
  const organizationId = useOrganization();
  const url = useSimUrl({ simDetailBaseUrl, orgId: organizationId, simId: hologramSimId });
  const usageByRecentSessionsActual = useOrgUrl({
    baseUrl: usageByRecentSessionUrl,
    orgId: organizationId,
  });

  const { dataSessions, reportingWindowSessions, zeroByteSessions } = useMemo(() => {
    const newDataSessions: Array<LegacyUsageDataRecord> = [];
    const newReportingWindowSessions: Array<LegacyUsageDataRecord> = [];
    const newZeroByteSessions: Array<LegacyUsageDataRecord> = [];

    usageData.forEach((record) => {
      if (record.bytes === 0) {
        newZeroByteSessions.push(record);
      } else if (record.is_reporting_window_with_usage === 1) {
        newReportingWindowSessions.push(record);
      } else {
        newDataSessions.push(record);
      }
    });

    return {
      dataSessions: newDataSessions,
      reportingWindowSessions: newReportingWindowSessions,
      zeroByteSessions: newZeroByteSessions,
    };
  }, [usageData]);

  const legendClickHandler = useCallback<LegendClickHandler>((_, legendItem, legend) => {
    const index = legendItem.datasetIndex;
    if (typeof index === 'undefined') {
      return;
    }

    const ci = legend.chart;
    const newHidden = !ci.getDatasetMeta(index).hidden;
    [ci.getDatasetMeta(index), ci.getDatasetMeta(index + 3)].forEach((meta) => {
      // eslint-disable-next-line no-param-reassign
      meta.hidden = newHidden;
    });
    ci.update();
  }, []);

  const data = useMemo<ChartData<'scatter' | 'bar', Array<{ x: number; y: number }>>>(
    () => ({
      datasets: [
        {
          label: 'Data session',
          data: dataSessions.map((record) => ({
            x: DateTime.fromFormat(record.timestamp, 'yyyy-MM-dd HH:mm:ss', {
              zone: 'utc',
            }).toMillis(),
            y: record.bytes,
          })),
          backgroundColor: '#099FC3',
          pointRadius: 4,
          pointStyle: 'circle',
          borderSkipped: true,
          order: 2,
        },
        {
          label: 'Reporting window',
          data: reportingWindowSessions.map((record) => ({
            x: DateTime.fromFormat(record.timestamp, 'yyyy-MM-dd HH:mm:ss', {
              zone: 'utc',
            }).toMillis(),
            y: record.bytes,
          })),
          backgroundColor: '#005D75',
          pointRadius: 4,
          pointStyle: 'rectRounded',
          order: 3,
        },
        {
          label: 'Zero-byte session',
          data: zeroByteSessions.map((record) => ({
            x: DateTime.fromFormat(record.timestamp, 'yyyy-MM-dd HH:mm:ss', {
              zone: 'utc',
            }).toMillis(),
            y: record.bytes,
          })),
          backgroundColor: '#2B2F3B',
          pointRadius: 5,
          pointStyle: 'triangle',
          order: 1,
        },
        {
          label: '',
          type: 'bar',
          data: dataSessions.map((record) => ({
            x: DateTime.fromFormat(record.timestamp, 'yyyy-MM-dd HH:mm:ss', {
              zone: 'utc',
            }).toMillis(),
            y: record.bytes,
          })),
          backgroundColor: '#6DDDF8',
          barPercentage: 1.0,
          barThickness: 1.5,
          borderSkipped: true,
          grouped: false,
          order: 5,
        },
        {
          label: '',
          type: 'bar',
          data: reportingWindowSessions.map((record) => ({
            x: DateTime.fromFormat(record.timestamp, 'yyyy-MM-dd HH:mm:ss', {
              zone: 'utc',
            }).toMillis(),
            y: record.bytes,
          })),
          borderColor: '#005D75',
          barPercentage: 1.0,
          barThickness: 1.5,
          borderSkipped: true,
          grouped: false,
          order: 6,
        },
        {
          label: '',
          type: 'bar',
          data: zeroByteSessions.map((record) => ({
            x: DateTime.fromFormat(record.timestamp, 'yyyy-MM-dd HH:mm:ss', {
              zone: 'utc',
            }).toMillis(),
            y: record.bytes,
          })),
          backgroundColor: '#BBBCC2',
          barPercentage: 1.0,
          barThickness: 1.5,
          borderSkipped: true,
          grouped: false,
          order: 4,
        },
      ],
    }),
    [dataSessions, reportingWindowSessions, zeroByteSessions]
  );

  const options = useMemo<ChartOptions<'scatter'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      font: {
        family: DdsTypographyFontFamilyDefault,
        size: 14,
        weight: 400,
        style: 'normal',
        lineHeight: '20px',
      },
      color: '#00040F',
      plugins: {
        legend: {
          display: true,
          position: 'top',
          align: 'start',
          onClick: legendClickHandler,
          labels: {
            color: '#00040F',
            usePointStyle: true,
            boxHeight: 8,
            boxWidth: 8,
            textAlign: 'left',
            filter: (legendItem) => legendItem.text !== '',
            sort: (a, b) => {
              switch (a.text) {
                case 'Data session':
                  return -1;
                case 'Zero-byte session':
                  return 1;
                default:
                  return b.text === 'Data session' ? 1 : -1;
              }
            },
          },
        },
        tooltip: {
          filter: (item) => item.dataset.label !== '',
          callbacks: {
            label: () => '',
            title: (tooltipItems) => {
              if (tooltipItems.length === 0) {
                return '';
              }
              let start: number | undefined;
              let end: number | undefined;

              tooltipItems.forEach((item) => {
                if (!end || (item.parsed?.x && item.parsed.x > end)) {
                  end = item.parsed.x;
                }
                if (!start || item.parsed?.x < start) {
                  start = item.parsed?.x;
                }
              });
              if (tooltipItems.length === 1) {
                return DateTime.fromMillis(tooltipItems[0].parsed?.x ?? 0, {
                  zone: 'utc',
                }).toFormat('MM-dd-yy HH:mm:ss ZZZZ');
              }
              if (start && end) {
                return `${DateTime.fromMillis(start, {
                  zone: 'utc',
                }).toFormat('MM-dd-yy HH:mm:ss ZZZZ')} - ${DateTime.fromMillis(end, {
                  zone: 'utc',
                }).toFormat('MM-dd-yy HH:mm:ss ZZZZ')}`;
              }

              return undefined;
            },
            footer: (tooltipItems) => {
              if (tooltipItems.length === 0) {
                return '';
              }
              const footerArr: Array<string> = [];

              const bytes = tooltipItems.reduce((acc, item) => acc + (item.parsed?.y ?? 0), 0);
              const bytesLabel = getUserFriendlyBytes(bytes);
              if (bytesLabel) {
                footerArr.push(bytesLabel);
              }

              if (tooltipItems.length > 1) {
                footerArr.push(`${tooltipItems.length} sessions`);
              } else if (tooltipItems[0].dataset.label) {
                footerArr.push(tooltipItems[0].dataset.label);
              }
              return footerArr;
            },
          },
          titleColor: '#ffffff',
          footerColor: '#ffffff',
          backgroundColor: '#00040F',
          titleAlign: 'center',
          footerAlign: 'center',
          titleFont: {
            family: DdsTypographyFontFamilyDefault,
            size: 14,
            weight: 400,
            style: 'normal',
          },
          footerFont: {
            family: DdsTypographyFontFamilyDefault,
            size: 14,
            weight: 500,
            style: 'normal',
          },
          mode: 'point',
          padding: 8,
        },
      },
      scales: {
        x: {
          grid: {
            color: '#E5E8EF',
            drawOnChartArea: false,
            tickColor: '#E5E8EF',
            tickWidth: 1,
          },
          // https://github.com/jkmnt/chartjs-scale-timestack?tab=readme-ov-file#options
          timestack: {
            datetime: { zone: 'utc' },
            max_density: 0.5,
            density: 0.25,
            format_style: { hour12: false, month: 'long', weekday: 'long', timeZone: 'UTC' },
          },
          type: 'timestack' as any,
          ticks: {
            font: {
              family: DdsTypographyFontFamilyDefault,
              size: 12,
              weight: 500,
              style: 'normal',
              lineHeight: '16px',
            },
            color: '#626D8D',
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: '#E5E8EF',
            drawTicks: false,
            tickWidth: 1,
          },
          ticks: {
            callback: (value) => getUserFriendlyBytes(value, 'short'),
            font: {
              family: DdsTypographyFontFamilyDefault,
              size: 12,
              weight: 500,
              style: 'normal',
              lineHeight: '16px',
            },
            color: '#626D8D',
            maxTicksLimit: 6,
            labelOffset: 2,
          },
        },
      },
    }),
    [legendClickHandler]
  );

  const id = useId();
  return (
    <Panel
      header={`Last ${formatNumber(usageData.length)} sessions`}
      headerActions={
        <Button variant="secondary" onClick={onLoadMoreClick} disabled={loading || !hasMore}>
          {`Load ${formatNumber(pageSize)} more sessions`}
        </Button>
      }
      isLoading={loading}
      footer={
        <>
          This chart displays up to 90 days of recent completed data sessions and reporting windows
          to help troubleshoot device connectivity behavior.{' '}
          <Link
            type="inline"
            to={mode === 'organization' ? usageByRecentSessionsActual! : `${url}/usage`}
          >
            View detailed usage reporting
          </Link>
        </>
      }
      footerVariant="alternative"
      noContentPadding
    >
      <div className={styles.contents}>
        <Scatter id={id} data={data as any} options={options} />
      </div>
    </Panel>
  );
};

export default SimRecentDataSessionsChartPanel;
